import type { ApplicationControllerGetApplicationsQueryParams } from '@/api/repository/repositoryComponents'
import {
	useApplicationControllerCreateApplication,
	useApplicationControllerGetApplications,
	useApplicationControllerGetApplicationWithParent,
	useApplicationControllerUpdate,
} from '@/api/repository/repositoryComponents'
import { DEFAULT_STALE_TIME } from '@/deprecated/services/constants'
import type { Application } from '@/shared/types'
import { useQueryClient } from '@tanstack/react-query'
import qs from 'qs'
import { toast } from 'react-hot-toast'

interface Props {
	workspaceId?: string
	filter?: {
		status?: ApplicationControllerGetApplicationsQueryParams['status']
	}
}

export const useApplications = ({ workspaceId, filter }: Props) => {
	const queryClient = useQueryClient()

	const {
		data: initialData,
		isLoading: isLoadingApps,
		error: errorLoadingApps,
	} = useApplicationControllerGetApplications(
		{
			// @ts-expect-error: Allows filters to be passed as an object in url
			queryParams: qs.stringify(filter, { skipNulls: true }),
		},
		{
			staleTime: DEFAULT_STALE_TIME,
			enabled: !workspaceId,
		}
	)

	const {
		data: workspaceApps,
		isLoading: isLoadingWorkspaceApps,
		error: errorLoadingWorkspaceApps,
	} = useApplicationControllerGetApplicationWithParent(
		{
			pathParams: {
				id: workspaceId,
			},
			// @ts-expect-error: Allows filters to be passed as an object in url
			queryParams: qs.stringify(filter, { skipNulls: true }),
		},
		{
			staleTime: DEFAULT_STALE_TIME,
			enabled: !!workspaceId,
		}
	)

	const applications: Array<Application> = ((workspaceId
		? workspaceApps?.data
		: initialData?.data) ?? []) as Array<Application>

	const createApplication = useApplicationControllerCreateApplication({
		onSuccess: () => {
			queryClient.invalidateQueries()
			toast.success('Application created')
		},
		onError: error => {
			toast.error(error.stack.message)
		},
	})

	const editApplication = useApplicationControllerUpdate({
		onSuccess: () => {
			queryClient.invalidateQueries()
			toast.success('Application edited')
		},
		onError: error => {
			toast.error(error.stack.message)
		},
	})

	return {
		applications,
		isLoading: isLoadingApps || isLoadingWorkspaceApps,
		createApplication,
		editApplication,
		error: errorLoadingApps ?? errorLoadingWorkspaceApps,
	}
}
